import { PriceBasisEnum } from '@msc-b2c/interfaces/enums';
import { ProductCarouselData, ServiceTypeOrderItem } from '@msc-b2c/interfaces/types';
import { isPerChildService } from '../../postbookingresults/utils/PostBookingResultsUtilities';

export function buildProductCarouPoolData(
  serviceTypesWithItemCodes: ServiceTypeOrderItem[] | any,
  matchedExcursionItemCodes: string[],
  matchedOBSItemCodes: Record<string, string[]>,
  productCarouselData: ProductCarouselData,
  isLowestPriceLogicEnabled: boolean
) {
  // Build per-service-type code pools
  const serviceTypePools = serviceTypesWithItemCodes.map((serviceType: any) => {
    if (serviceType.type === 'EXCURSIONS') {
      return {
        type: serviceType.type,
        itemCodes: [...new Set(matchedExcursionItemCodes)], // dedup exc items
        fallbackCodes: selectFallBackCodes(
          isLowestPriceLogicEnabled,
          productCarouselData,
          serviceType.type,
          matchedExcursionItemCodes
        ),
      };
    } else {
      const matched = matchedOBSItemCodes[serviceType.type] ?? [];
      return {
        type: serviceType.type,
        itemCodes: [...new Set(matched)], // dedup obs items
        fallbackCodes: selectFallBackCodes(
          isLowestPriceLogicEnabled,
          productCarouselData,
          serviceType.type,
          matched
        ),
      };
    }
  });

  return serviceTypePools;
}

// Logic for selecting fall back codes (in case no item codes are configured)
// 1. Normal product carousel mode -> fallback are selected based on the order of API
// 2. "You might like this also" mode -> fallback codes are sorted based on the lowest item price (a.k.a the afterdiscount item)
function selectFallBackCodes(
  isLowestPriceLogicEnabled: boolean,
  productCarouselData: ProductCarouselData,
  type: string,
  matchedExcursionItemCodes?: string[],
  matched?: string[]
) {
  const isExcursion = type === 'EXCURSIONS';

  const items = isExcursion
    ? productCarouselData.excursionsData.filter(
        (i) =>
          !!i.code &&
          !matchedExcursionItemCodes?.includes(i.code) &&
          Number(i.inventory?.numberAvailable) !== 0
      )
    : productCarouselData.obsData.filter(
        (i) => i.type.code === type && !matched?.includes(i.code ?? '')
      );

  if (isLowestPriceLogicEnabled) {
    items.sort((a, b) =>
      isExcursion ? getExcursionPrice(a) - getExcursionPrice(b) : getObsPrice(a) - getObsPrice(b)
    );
  }

  return items.map((i) => i.code).filter((code): code is string => !!code);
}

// price extraction helpers

// get excursion prices based on basis (per cabin or per person)
function getExcursionPrice(item: any): number {
  if (item.priceBasis === PriceBasisEnum.PER_CABIN) {
    return Number(item.prices?.perCabin?.total?.afterDiscount) || 0;
  }
  return Number(item.prices?.perAdult?.total?.afterDiscount) || 0;
}

// for combined ALLINC we can consider adult price for sorting
function getObsPrice(item: any): number {
  if (isPerChildService(item) && item.priceBasis === PriceBasisEnum.PER_PERSON) {
    return Number(item.prices?.perChild?.total?.afterDiscount) || 0;
  }
  if (item.priceBasis === PriceBasisEnum.PER_CABIN) {
    return Number(item.prices?.perCabin?.total?.afterDiscount) || 0;
  }
  return Number(item.prices?.perAdult?.total?.afterDiscount) || 0;
}
